/**
 * Cross-domain cookie utilities for Viubux
 * These cookies can be shared between connect.viubux.com and play.viubux.com
 */

/**
 * Set a cookie that can be read by all *.viubux.com subdomains
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days (default 30)
 */
export const setCrossDomainCookie = (name, value, days = 30) => {
  const maxAge = days * 24 * 60 * 60;

  // Domain=.viubux.com allows play.viubux.com to read the cookie
  // In development/preview, we use the current domain only
  const isProduction = window.location.hostname.includes('viubux.com');
  const domain = isProduction ? '; Domain=.viubux.com' : '';

  // Max-Age controls expiry; SameSite=Lax + Secure required for cross-subdomain
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure${domain}`;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
  const isProduction = window.location.hostname.includes('viubux.com');
  const domain = isProduction ? '; Domain=.viubux.com' : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/${domain}`;
};

/**
 * Set referral code cookie (vbx_ref) with timestamp
 * This cookie is readable by play.viubux.com
 * Expires in 90 days
 * @param {string} referralCode - The referral code/username
 */
export const setReferralCookie = (referralCode) => {
  if (!referralCode) return;
  
  setCrossDomainCookie('vbx_ref', referralCode, 90);
  setCrossDomainCookie('vbx_ref_ts', Date.now().toString(), 90);
};

/**
 * Get valid referral code from cookie (not expired - 90 days)
 * @returns {string} - Valid referral code or empty string
 */
export const getValidReferralFromCookie = () => {
  const cookieRef = getCookie('vbx_ref');
  const cookieTs = getCookie('vbx_ref_ts');
  
  if (cookieRef && cookieTs) {
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    const isExpired = Date.now() - parseInt(cookieTs) > ninetyDaysMs;
    
    if (!isExpired) {
      return cookieRef;
    } else {
      // Clean up expired cookies
      deleteCookie('vbx_ref');
      deleteCookie('vbx_ref_ts');
    }
  }
  
  return '';
};

/**
 * Clear referral cookies
 */
export const clearReferralCookie = () => {
  deleteCookie('vbx_ref');
  deleteCookie('vbx_ref_ts');
};
